import React, { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../hooks/useAuth';
import authService from '../services/authService';
import styles from './HomePage.module.scss';
import loginStyles from './LoginPage.module.scss';
import signupStyles from './SignupPage.module.scss';

type AuthView = 'login' | 'signup' | 'forgot-password' | 'reset-password';

// --- Form validation (production-grade) ---
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PASSWORD_STRONG = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;
const OTP_REGEX = /^\d{6}$/;

function validateEmail(value: string): string | null {
  const v = value.trim().toLowerCase();
  if (!v) return 'Email is required';
  if (!EMAIL_REGEX.test(v)) return 'Enter a valid email address';
  return null;
}

function validatePassword(value: string): string | null {
  if (!value) return 'Password is required';
  if (!PASSWORD_STRONG.test(value)) return 'Min 8 characters, with uppercase, lowercase and a number';
  return null;
}

function validateRequired(value: string, name: string, minLength = 1): string | null {
  const v = value.trim();
  if (!v) return `${name} is required`;
  if (minLength > 1 && v.length < minLength) return `${name} must be at least ${minLength} characters`;
  return null;
}

function validateOtp(value: string): string | null {
  const v = value.trim();
  if (!v) return 'OTP is required';
  if (!OTP_REGEX.test(v)) return 'Enter the 6-digit code';
  return null;
}

const HomePage: React.FC = () => {
  const { theme, toggleTheme } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const authView = (searchParams.get('auth') as AuthView) || null;
  const isValidAuth = authView === 'login' || authView === 'signup' || authView === 'forgot-password' || authView === 'reset-password';
  const showAuthForm = isValidAuth && authView;

  const { login, register, loading, error, clearError } = useAuth();

  const setAuthView = (view: AuthView | null, email?: string) => {
    if (view) {
      if (view === 'reset-password' && email) setSearchParams({ auth: view, email });
      else setSearchParams({ auth: view });
    } else {
      setSearchParams({}, { replace: true });
    }
  };

  // Login form state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loginTouched, setLoginTouched] = useState({ email: false, password: false });

  // Signup form state
  const [signupFullName, setSignupFullName] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupCompany, setSignupCompany] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupConfirmPassword, setSignupConfirmPassword] = useState('');
  const [signupFormError, setSignupFormError] = useState<string | null>(null);
  const [signupTouched, setSignupTouched] = useState({
    fullName: false,
    email: false,
    company: false,
    password: false,
    confirmPassword: false,
  });

  // Forgot password state
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState<string | null>(null);
  const [forgotMessage, setForgotMessage] = useState<string | null>(null);
  const [forgotTouched, setForgotTouched] = useState(false);

  useEffect(() => {
    if (!error) return;
    const t = setTimeout(() => clearError(), 4000);
    return () => clearTimeout(t);
  }, [error, clearError]);

  useEffect(() => {
    if (!signupFormError) return;
    const t = setTimeout(() => setSignupFormError(null), 4000);
    return () => clearTimeout(t);
  }, [signupFormError]);

  useEffect(() => {
    if (!forgotError && !forgotMessage) return;
    const t = setTimeout(() => {
      setForgotError(null);
      setForgotMessage(null);
    }, 4000);
    return () => clearTimeout(t);
  }, [forgotError, forgotMessage]);

  const onLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await login({ email: loginEmail.trim().toLowerCase(), password: loginPassword });
      navigate('/dashboard');
    } catch (err) {
      console.error('Login failed:', err);
    }
  };

  const onSignupSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSignupFormError(null);
    const trimmedEmail = signupEmail.trim().toLowerCase();
    const pwd = signupPassword.trim();
    const confirm = signupConfirmPassword.trim();
    if (pwd !== confirm) {
      setSignupFormError('Passwords do not match');
      return;
    }
    const strongPasswordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;
    if (!strongPasswordRegex.test(pwd)) {
      setSignupFormError('Password must be at least 8 characters and include uppercase, lowercase, and a number.');
      return;
    }
    const nameParts = signupFullName.trim().split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';
    try {
      await register({ email: trimmedEmail, password: pwd, firstName, lastName, companyName: signupCompany.trim() });
      navigate('/dashboard');
    } catch (err) {
      console.error('Registration failed:', err);
    }
  };

  const onForgotSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = forgotEmail.trim().toLowerCase();
    setForgotError(null);
    setForgotMessage(null);
    setForgotLoading(true);
    try {
      const res = await authService.requestPasswordReset({ email });
      setForgotMessage(res.message || 'If that email is registered, we have sent an OTP to reset your password.');
      setAuthView('reset-password', email);
    } catch (err: any) {
      setForgotError(err?.message || 'Failed to request password reset');
    } finally {
      setForgotLoading(false);
    }
  };

  // Reset password form state (email from URL when auth=reset-password)
  const resetEmailParam = searchParams.get('email') || '';
  const [resetEmail, setResetEmail] = useState(resetEmailParam);
  const [resetOtp, setResetOtp] = useState('');
  const [resetNewPassword, setResetNewPassword] = useState('');
  const [resetConfirmPassword, setResetConfirmPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetResendLoading, setResetResendLoading] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const [resetMessage, setResetMessage] = useState<string | null>(null);
  const [resetTouched, setResetTouched] = useState({
    email: false,
    otp: false,
    newPassword: false,
    confirmPassword: false,
  });

  const resetNormalizedEmail = resetEmail.trim().toLowerCase();

  // --- Validation: errors (only meaningful when field touched) ---
  const loginEmailError = loginTouched.email ? validateEmail(loginEmail) : null;
  const loginPasswordError = loginTouched.password ? (loginPassword ? null : 'Password is required') : null;
  const loginFormValid = !validateEmail(loginEmail) && !!loginPassword.trim();

  const signupFullNameError = signupTouched.fullName ? validateRequired(signupFullName, 'Full name', 2) : null;
  const signupEmailError = signupTouched.email ? validateEmail(signupEmail) : null;
  const signupCompanyError = signupTouched.company ? validateRequired(signupCompany, 'Company') : null;
  const signupPasswordError = signupTouched.password ? validatePassword(signupPassword) : null;
  const signupConfirmError = signupTouched.confirmPassword
    ? (signupConfirmPassword ? (signupPassword === signupConfirmPassword ? null : 'Passwords do not match') : 'Confirm password is required')
    : null;
  const signupFormValid =
    !validateRequired(signupFullName, 'Full name', 2) &&
    !validateEmail(signupEmail) &&
    !validateRequired(signupCompany, 'Company') &&
    !validatePassword(signupPassword) &&
    !!signupConfirmPassword.trim() &&
    signupPassword === signupConfirmPassword;

  const forgotEmailError = forgotTouched ? validateEmail(forgotEmail) : null;
  const forgotFormValid = !validateEmail(forgotEmail);

  const resetEmailError = resetTouched.email ? validateEmail(resetEmail) : null;
  const resetOtpError = resetTouched.otp ? validateOtp(resetOtp) : null;
  const resetNewPasswordError = resetTouched.newPassword ? validatePassword(resetNewPassword) : null;
  const resetConfirmError = resetTouched.confirmPassword
    ? (resetConfirmPassword ? (resetNewPassword === resetConfirmPassword ? null : 'Passwords do not match') : 'Confirm password is required')
    : null;
  const resetFormValid =
    !validateEmail(resetEmail) &&
    !validateOtp(resetOtp) &&
    !validatePassword(resetNewPassword) &&
    !!resetConfirmPassword.trim() &&
    resetNewPassword === resetConfirmPassword;

  useEffect(() => {
    if (authView === 'reset-password' && resetEmailParam) setResetEmail(resetEmailParam);
  }, [authView, resetEmailParam]);

  useEffect(() => {
    if (!showAuthForm) return;
    // Keep auth openers simple: always take user to the top first.
    const timer = window.setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 40);
    return () => window.clearTimeout(timer);
  }, [showAuthForm, authView]);

  useEffect(() => {
    if (!resetError && !resetMessage) return;
    const t = setTimeout(() => {
      setResetError(null);
      setResetMessage(null);
    }, 4000);
    return () => clearTimeout(t);
  }, [resetError, resetMessage]);

  const onResetSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetError(null);
    setResetMessage(null);
    if (resetNewPassword !== resetConfirmPassword) {
      setResetError('Passwords do not match');
      return;
    }
    setResetLoading(true);
    try {
      const res = await authService.resetPassword({
        email: resetNormalizedEmail,
        otp: resetOtp.trim(),
        newPassword: resetNewPassword,
      });
      setResetMessage(res.message || 'Password has been reset successfully.');
      setTimeout(() => setAuthView('login'), 600);
    } catch (err: any) {
      setResetError(err?.message || 'Failed to reset password');
    } finally {
      setResetLoading(false);
    }
  };

  const onResendOtp = async () => {
    if (!resetNormalizedEmail) return;
    setResetError(null);
    setResetMessage(null);
    setResetResendLoading(true);
    try {
      const res = await authService.requestPasswordReset({ email: resetNormalizedEmail });
      setResetMessage(res.message || 'If that email is registered, we have sent a new OTP.');
    } catch (err: any) {
      setResetError(err?.message || 'Failed to resend OTP');
    } finally {
      setResetResendLoading(false);
    }
  };

  const differentiators = [
    { title: 'Grounded answers only', desc: 'Responses are generated from approved company data. When context is missing, the assistant says so instead of guessing.' },
    { title: 'Citations your team can trust', desc: 'Every response includes references to the exact row, file fragment, or policy section behind the answer.' },
    { title: 'Private by architecture', desc: 'Workspace-level isolation, encrypted transport/storage, and strict boundaries so your data never trains shared public models.' },
    { title: 'Consistent knowledge at scale', desc: 'Support, operations, and leadership get one reliable version of the answer across teams and channels.' },
    { title: 'Governance built in', desc: 'Role-based controls, audit trails, and usage analytics support enterprise readiness from day one.' },
  ];

  const outcomes = [
    'Lower average response time',
    'Higher first-response accuracy',
    'Less repetitive support work',
    'Faster onboarding for new hires',
    'More confident operational decisions',
    'Clear visibility into knowledge gaps',
  ];

  const audiences = [
    'Customer support & success teams',
    'Operations and process teams',
    'IT, security, and compliance',
    'Mid-market and enterprise SaaS',
  ];

  const hostingOptions = [
    { title: 'Managed cloud', desc: 'Fastest path to launch with secure multi-tenant architecture and zero infrastructure overhead.' },
    { title: 'Dedicated private cloud', desc: 'Isolated runtime with predictable performance and stronger tenant-level control.' },
    { title: 'Deploy to your cloud', desc: 'Run in your AWS, GCP, or Azure environment with your networking and security policies.' },
    { title: 'On-premises', desc: 'Host in your data center or private cloud for strict residency and air-gapped requirements.' },
  ];

  const trustMetrics = [
    { value: '< 2 weeks', label: 'Typical go-live window' },
    { value: '99.9%', label: 'Platform availability target' },
    { value: '24/7', label: 'Monitoring and alerts' },
  ];

  const complianceBadges = ['Workspace isolation', 'End-to-end encryption', 'Role-based permissions', 'Audit logs'];
  const useCasePoints = [...audiences, ...complianceBadges];

  const pipelineSteps = [
    {
      icon: '📚',
      title: 'Ingest',
      body: 'Sync databases, documents, and internal tools into a structured knowledge layer.',
    },
    {
      icon: '🔍',
      title: 'Retrieve',
      body: 'Semantic retrieval fetches only relevant context while enforcing role-based access.',
    },
    {
      icon: '⚡',
      title: 'Generate',
      body: 'Answers are generated with source references so teams can verify every response.',
    },
  ];

  const pipelineSignals = ['Low hallucination risk', 'Source citations', 'Tenant isolation'];

  return (
    <div className={styles.homePage}>
      <header className={styles.header}>
        <div className={styles.headerInner}>
          <Link to="/" className={styles.logoArea}>
            <img
              src={`${process.env.PUBLIC_URL || ''}/arihex-logo-${theme === 'dark' ? 'dark' : 'light'}.png`}
              alt="Arihex"
              className={styles.logoImg}
            />
            <span className={styles.logoTagline}>Precision • AI • Innovation</span>
          </Link>

          <nav className={styles.nav}>
            <a href="#product" className={styles.navLink}>Product</a>
            <a href="#how-it-works" className={styles.navLink}>How it works</a>
            <a href="#use-cases" className={styles.navLink}>Use cases</a>
            <Link to="/about" className={styles.navLink}>About</Link>
          </nav>

          <div className={styles.headerActions}>
            <button
              type="button"
              className={styles.themeToggleBtn}
              onClick={toggleTheme}
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? '☀️' : '🌙'}
            </button>
            <Link to="/?auth=login" className={styles.headerLink}>
              Sign in
            </Link>
            <Link to="/?auth=signup" className={styles.headerCta}>
              Get started
            </Link>
          </div>
        </div>
      </header>

      <main className={styles.main}>
        <section className={styles.hero}>
          <div className={styles.heroContent}>
            <p className={styles.heroBadge}>Enterprise-ready RAG platform</p>
            <h1 className={styles.heroTitle}>
              Turn company knowledge into{" "}
              <span className={styles.heroHighlight}>trusted AI answers.</span>
            </h1>
            <p className={styles.heroSubtitle}>
              Arihex connects your databases, spreadsheets, documents, and internal systems to deliver
              retrieval-augmented assistants with citations, guardrails, and governance your teams can rely on.
            </p>
            <div className={styles.heroActions}>
              <Link to="/?auth=signup" className={styles.heroPrimary}>
                Start your workspace
              </Link>
              <Link to="/contact?category=demo" className={`${styles.heroSecondary} ${styles.heroDemoCta}`}>
                Book demo
              </Link>
              <Link to="/?auth=login" className={styles.heroSecondary}>
                Sign in to your workspace
              </Link>
            </div>
            <p className={styles.heroFootnote}>
              No public model training on your data. Every answer is grounded in approved internal sources.
            </p>
            <div className={styles.dataStrip}>
              <span className={styles.dataLabel}>Integrations</span>
              <span>PostgreSQL</span>
              <span>MySQL</span>
              <span>Excel</span>
              <span>CSV</span>
              <span>PDF</span>
            </div>
            <div className={styles.metricStrip}>
              {trustMetrics.map((metric) => (
                <article key={metric.label} className={styles.metricCard}>
                  <p className={styles.metricValue}>{metric.value}</p>
                  <p className={styles.metricLabel}>{metric.label}</p>
                </article>
              ))}
            </div>
          </div>

          <div className={styles.heroPanel}>
            {showAuthForm ? (
              <>
                <div className={styles.heroPanelHeader}>
                  <span className={styles.heroPanelLabel}>
                    {authView === 'login' && 'Sign in'}
                    {authView === 'signup' && 'Get started'}
                    {authView === 'forgot-password' && 'Forgot password'}
                    {authView === 'reset-password' && 'Reset password'}
                  </span>
                  <button
                    type="button"
                    className={styles.heroPanelBack}
                    onClick={() => setAuthView(null)}
                    aria-label="Close auth panel"
                  >
                    ✕
                  </button>
                </div>
                <div className={styles.heroPanelFormWrap}>
                  {authView === 'login' && (
                    <div className={styles.heroPanelFormCompact}>
                      <p className={loginStyles.authFormSubtitle}>Sign in to your account to continue</p>
                      {error && <div className={loginStyles.errorMessage} role="alert">{error}</div>}
                      <form className={loginStyles.authForm} onSubmit={onLoginSubmit} noValidate>
                        <div className={`${loginStyles.formGroup} ${loginEmailError ? styles.formGroupInvalid : loginTouched.email && !loginEmailError ? styles.formGroupValid : ''}`}>
                          <label className={loginStyles.formLabel} htmlFor="hp-email">Email</label>
                          <div className={loginStyles.formInputWrapper}>
                            <span className={loginStyles.formIcon}>✉️</span>
                            <input
                              id="hp-email"
                              type="email"
                              className={loginStyles.formInput}
                              placeholder="john@example.com"
                              autoComplete="email"
                              value={loginEmail}
                              onChange={(e) => setLoginEmail(e.target.value)}
                              onBlur={() => setLoginTouched((t) => ({ ...t, email: true }))}
                              disabled={loading}
                              aria-invalid={!!loginEmailError}
                              aria-describedby={loginEmailError ? 'hp-email-err' : undefined}
                            />
                          </div>
                          {loginEmailError && <span id="hp-email-err" className={styles.fieldError} role="alert">{loginEmailError}</span>}
                        </div>
                        <div className={`${loginStyles.formGroup} ${loginPasswordError ? styles.formGroupInvalid : loginTouched.password && !loginPasswordError ? styles.formGroupValid : ''}`}>
                          <label className={loginStyles.formLabel} htmlFor="hp-password">Password</label>
                          <div className={loginStyles.formInputWrapper}>
                            <span className={loginStyles.formIcon}>🔒</span>
                            <input
                              id="hp-password"
                              type="password"
                              className={loginStyles.formInput}
                              placeholder="••••••••"
                              autoComplete="current-password"
                              value={loginPassword}
                              onChange={(e) => setLoginPassword(e.target.value)}
                              onBlur={() => setLoginTouched((t) => ({ ...t, password: true }))}
                              disabled={loading}
                              aria-invalid={!!loginPasswordError}
                              aria-describedby={loginPasswordError ? 'hp-password-err' : undefined}
                            />
                          </div>
                          {loginPasswordError && <span id="hp-password-err" className={styles.fieldError} role="alert">{loginPasswordError}</span>}
                        </div>
                        <div className={loginStyles.formRemember}>
                          <input
                            id="hp-remember"
                            type="checkbox"
                            className={loginStyles.checkboxInput}
                            checked={rememberMe}
                            onChange={(e) => setRememberMe(e.target.checked)}
                            disabled={loading}
                          />
                          <label htmlFor="hp-remember" className={loginStyles.checkboxLabel}>Remember me</label>
                        </div>
                        <button type="submit" className={`${loginStyles.btnFull} btn btn-primary`} disabled={loading || !loginFormValid}>
                          {loading ? 'Signing In...' : 'Sign In'}
                        </button>
                      </form>
                      <div className={loginStyles.authToggle}>
                        <span>Don&apos;t have an account? <button type="button" className={loginStyles.linkBtn} onClick={() => setAuthView('signup')} disabled={loading}>Sign up</button></span>
                      </div>
                      <div className={loginStyles.authFooter}>
                        <button type="button" className={loginStyles.linkBtn} onClick={() => setAuthView('forgot-password')} disabled={loading}>Forgot password?</button>
                      </div>
                    </div>
                  )}
                  {authView === 'signup' && (
                    <div className={styles.heroPanelFormCompact}>
                      <p className={signupStyles.authFormSubtitle}>Sign up to get started</p>
                      {(signupFormError || error) && (
                        <div className={signupStyles.errorMessage} role="alert">{signupFormError || error}</div>
                      )}
                      <form className={signupStyles.authForm} onSubmit={onSignupSubmit} noValidate>
                        <div className={`${signupStyles.formGroup} ${signupFullNameError ? styles.formGroupInvalid : signupTouched.fullName && !signupFullNameError ? styles.formGroupValid : ''}`}>
                          <label className={signupStyles.formLabel} htmlFor="hp-fullName">Full Name</label>
                          <div className={signupStyles.formInputWrapper}>
                            <span className={signupStyles.formIcon}>👤</span>
                            <input id="hp-fullName" type="text" className={signupStyles.formInput} placeholder="John Doe" autoComplete="name" value={signupFullName} onChange={(e) => setSignupFullName(e.target.value)} onBlur={() => setSignupTouched((t) => ({ ...t, fullName: true }))} disabled={loading} aria-invalid={!!signupFullNameError} aria-describedby={signupFullNameError ? 'hp-fullName-err' : undefined} />
                          </div>
                          {signupFullNameError && <span id="hp-fullName-err" className={styles.fieldError} role="alert">{signupFullNameError}</span>}
                        </div>
                        <div className={`${signupStyles.formGroup} ${signupEmailError ? styles.formGroupInvalid : signupTouched.email && !signupEmailError ? styles.formGroupValid : ''}`}>
                          <label className={signupStyles.formLabel} htmlFor="hp-signup-email">Email</label>
                          <div className={signupStyles.formInputWrapper}>
                            <span className={signupStyles.formIcon}>✉️</span>
                            <input id="hp-signup-email" type="email" className={signupStyles.formInput} placeholder="john@example.com" autoComplete="email" value={signupEmail} onChange={(e) => setSignupEmail(e.target.value)} onBlur={() => setSignupTouched((t) => ({ ...t, email: true }))} disabled={loading} aria-invalid={!!signupEmailError} aria-describedby={signupEmailError ? 'hp-signup-email-err' : undefined} />
                          </div>
                          {signupEmailError && <span id="hp-signup-email-err" className={styles.fieldError} role="alert">{signupEmailError}</span>}
                        </div>
                        <div className={`${signupStyles.formGroup} ${signupCompanyError ? styles.formGroupInvalid : signupTouched.company && !signupCompanyError ? styles.formGroupValid : ''}`}>
                          <label className={signupStyles.formLabel} htmlFor="hp-company">Company</label>
                          <div className={signupStyles.formInputWrapper}>
                            <span className={signupStyles.formIcon}>🏢</span>
                            <input id="hp-company" type="text" className={signupStyles.formInput} placeholder="Acme Inc" autoComplete="organization" value={signupCompany} onChange={(e) => setSignupCompany(e.target.value)} onBlur={() => setSignupTouched((t) => ({ ...t, company: true }))} disabled={loading} aria-invalid={!!signupCompanyError} aria-describedby={signupCompanyError ? 'hp-company-err' : undefined} />
                          </div>
                          {signupCompanyError && <span id="hp-company-err" className={styles.fieldError} role="alert">{signupCompanyError}</span>}
                        </div>
                        <div className={`${signupStyles.formGroup} ${signupPasswordError ? styles.formGroupInvalid : signupTouched.password && !signupPasswordError ? styles.formGroupValid : ''}`}>
                          <label className={signupStyles.formLabel} htmlFor="hp-signup-pwd">Password</label>
                          <div className={signupStyles.formInputWrapper}>
                            <span className={signupStyles.formIcon}>🔒</span>
                            <input id="hp-signup-pwd" type="password" className={signupStyles.formInput} placeholder="••••••••" autoComplete="new-password" value={signupPassword} onChange={(e) => setSignupPassword(e.target.value)} onBlur={() => setSignupTouched((t) => ({ ...t, password: true }))} disabled={loading} aria-invalid={!!signupPasswordError} aria-describedby={signupPasswordError ? 'hp-signup-pwd-err' : undefined} />
                          </div>
                          {signupPasswordError && <span id="hp-signup-pwd-err" className={styles.fieldError} role="alert">{signupPasswordError}</span>}
                        </div>
                        <div className={`${signupStyles.formGroup} ${signupConfirmError ? styles.formGroupInvalid : signupTouched.confirmPassword && !signupConfirmError ? styles.formGroupValid : ''}`}>
                          <label className={signupStyles.formLabel} htmlFor="hp-confirm">Confirm Password</label>
                          <div className={signupStyles.formInputWrapper}>
                            <span className={signupStyles.formIcon}>✅</span>
                            <input id="hp-confirm" type="password" className={signupStyles.formInput} placeholder="••••••••" autoComplete="new-password" value={signupConfirmPassword} onChange={(e) => setSignupConfirmPassword(e.target.value)} onBlur={() => setSignupTouched((t) => ({ ...t, confirmPassword: true }))} disabled={loading} aria-invalid={!!signupConfirmError} aria-describedby={signupConfirmError ? 'hp-confirm-err' : undefined} />
                          </div>
                          {signupConfirmError && <span id="hp-confirm-err" className={styles.fieldError} role="alert">{signupConfirmError}</span>}
                        </div>
                        <button type="submit" className={`${signupStyles.btnFull} btn btn-primary`} disabled={loading || !signupFormValid}>
                          {loading ? 'Creating Account...' : 'Create Account'}
                        </button>
                      </form>
                      <div className={signupStyles.authToggle}>
                        <span>Already have an account? <button type="button" className={signupStyles.linkBtn} onClick={() => setAuthView('login')} disabled={loading}>Sign in</button></span>
                      </div>
                    </div>
                  )}
                  {authView === 'forgot-password' && (
                    <div className={styles.heroPanelFormCompact}>
                      <p className={loginStyles.authFormSubtitle}>Enter your email to receive a reset code.</p>
                      {forgotError && <div className={loginStyles.errorMessage} role="alert">{forgotError}</div>}
                      {forgotMessage && <div className="app-success">{forgotMessage}</div>}
                      <form className={loginStyles.authForm} onSubmit={onForgotSubmit} noValidate>
                        <div className={`${loginStyles.formGroup} ${forgotEmailError ? styles.formGroupInvalid : forgotTouched && !forgotEmailError ? styles.formGroupValid : ''}`}>
                          <label className={loginStyles.formLabel} htmlFor="hp-forgot-email">Email</label>
                          <div className={loginStyles.formInputWrapper}>
                            <span className={loginStyles.formIcon}>✉️</span>
                            <input
                              id="hp-forgot-email"
                              type="email"
                              className={loginStyles.formInput}
                              placeholder="john@example.com"
                              autoComplete="email"
                              value={forgotEmail}
                              onChange={(e) => setForgotEmail(e.target.value)}
                              onBlur={() => setForgotTouched(true)}
                              disabled={forgotLoading}
                              aria-invalid={!!forgotEmailError}
                              aria-describedby={forgotEmailError ? 'hp-forgot-email-err' : undefined}
                            />
                          </div>
                          {forgotEmailError && <span id="hp-forgot-email-err" className={styles.fieldError} role="alert">{forgotEmailError}</span>}
                        </div>
                        <button type="submit" className={`${loginStyles.btnFull} btn btn-primary`} disabled={forgotLoading || !forgotFormValid}>
                          {forgotLoading ? 'Sending code...' : 'Send OTP'}
                        </button>
                      </form>
                      <div className={loginStyles.authToggle}>
                        <span>Remembered your password? <button type="button" className={loginStyles.linkBtn} onClick={() => setAuthView('login')} disabled={forgotLoading}>Sign in</button></span>
                      </div>
                      <div className={loginStyles.authFooter}>
                        <button type="button" className={loginStyles.linkBtn} onClick={() => setAuthView('reset-password', forgotEmail.trim().toLowerCase())} disabled={forgotLoading}>Already have a code? Reset password</button>
                      </div>
                    </div>
                  )}
                  {authView === 'reset-password' && (
                    <div className={styles.heroPanelFormCompact}>
                      <p className={loginStyles.authFormSubtitle}>Enter your OTP and choose a new password.</p>
                      {resetError && <div className={loginStyles.errorMessage} role="alert">{resetError}</div>}
                      {resetMessage && <div className="app-success">{resetMessage}</div>}
                      <form className={loginStyles.authForm} onSubmit={onResetSubmit} noValidate>
                        <div className={`${loginStyles.formGroup} ${resetEmailError ? styles.formGroupInvalid : resetTouched.email && !resetEmailError ? styles.formGroupValid : ''}`}>
                          <label className={loginStyles.formLabel} htmlFor="hp-reset-email">Email</label>
                          <div className={loginStyles.formInputWrapper}>
                            <span className={loginStyles.formIcon}>✉️</span>
                            <input
                              id="hp-reset-email"
                              type="email"
                              className={loginStyles.formInput}
                              placeholder="john@example.com"
                              autoComplete="email"
                              value={resetEmail}
                              onChange={(e) => setResetEmail(e.target.value)}
                              onBlur={() => setResetTouched((t) => ({ ...t, email: true }))}
                              disabled={resetLoading}
                              aria-invalid={!!resetEmailError}
                              aria-describedby={resetEmailError ? 'hp-reset-email-err' : undefined}
                            />
                          </div>
                          {resetEmailError && <span id="hp-reset-email-err" className={styles.fieldError} role="alert">{resetEmailError}</span>}
                        </div>
                        <div className={`${loginStyles.formGroup} ${resetOtpError ? styles.formGroupInvalid : resetTouched.otp && !resetOtpError ? styles.formGroupValid : ''}`}>
                          <label className={loginStyles.formLabel} htmlFor="hp-reset-otp">OTP</label>
                          <div className={loginStyles.formInputWrapper}>
                            <span className={loginStyles.formIcon}>🔢</span>
                            <input
                              id="hp-reset-otp"
                              inputMode="numeric"
                              autoComplete="one-time-code"
                              className={loginStyles.formInput}
                              placeholder="6-digit code"
                              maxLength={6}
                              value={resetOtp}
                              onChange={(e) => setResetOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                              onBlur={() => setResetTouched((t) => ({ ...t, otp: true }))}
                              disabled={resetLoading}
                              aria-invalid={!!resetOtpError}
                              aria-describedby={resetOtpError ? 'hp-reset-otp-err' : undefined}
                            />
                          </div>
                          {resetOtpError && <span id="hp-reset-otp-err" className={styles.fieldError} role="alert">{resetOtpError}</span>}
                        </div>
                        <div className={`${loginStyles.formGroup} ${resetNewPasswordError ? styles.formGroupInvalid : resetTouched.newPassword && !resetNewPasswordError ? styles.formGroupValid : ''}`}>
                          <label className={loginStyles.formLabel} htmlFor="hp-reset-newpwd">New Password</label>
                          <div className={loginStyles.formInputWrapper}>
                            <span className={loginStyles.formIcon}>🔒</span>
                            <input
                              id="hp-reset-newpwd"
                              type="password"
                              className={loginStyles.formInput}
                              placeholder="••••••••"
                              autoComplete="new-password"
                              value={resetNewPassword}
                              onChange={(e) => setResetNewPassword(e.target.value)}
                              onBlur={() => setResetTouched((t) => ({ ...t, newPassword: true }))}
                              disabled={resetLoading}
                              aria-invalid={!!resetNewPasswordError}
                              aria-describedby={resetNewPasswordError ? 'hp-reset-newpwd-err' : undefined}
                            />
                          </div>
                          {resetNewPasswordError && <span id="hp-reset-newpwd-err" className={styles.fieldError} role="alert">{resetNewPasswordError}</span>}
                        </div>
                        <div className={`${loginStyles.formGroup} ${resetConfirmError ? styles.formGroupInvalid : resetTouched.confirmPassword && !resetConfirmError ? styles.formGroupValid : ''}`}>
                          <label className={loginStyles.formLabel} htmlFor="hp-reset-confirm">Confirm Password</label>
                          <div className={loginStyles.formInputWrapper}>
                            <span className={loginStyles.formIcon}>✅</span>
                            <input
                              id="hp-reset-confirm"
                              type="password"
                              className={loginStyles.formInput}
                              placeholder="••••••••"
                              autoComplete="new-password"
                              value={resetConfirmPassword}
                              onChange={(e) => setResetConfirmPassword(e.target.value)}
                              onBlur={() => setResetTouched((t) => ({ ...t, confirmPassword: true }))}
                              disabled={resetLoading}
                              aria-invalid={!!resetConfirmError}
                              aria-describedby={resetConfirmError ? 'hp-reset-confirm-err' : undefined}
                            />
                          </div>
                          {resetConfirmError && <span id="hp-reset-confirm-err" className={styles.fieldError} role="alert">{resetConfirmError}</span>}
                        </div>
                        <button type="submit" className={`${loginStyles.btnFull} btn btn-primary`} disabled={resetLoading || !resetFormValid}>
                          {resetLoading ? 'Resetting...' : 'Reset Password'}
                        </button>
                      </form>
                      <div className={loginStyles.authToggle}>
                        <span>Need a new OTP? <button type="button" className={loginStyles.linkBtn} onClick={onResendOtp} disabled={resetLoading || resetResendLoading || !resetNormalizedEmail}>
                          {resetResendLoading ? 'Sending...' : 'Resend OTP'}
                        </button></span>
                      </div>
                      <div className={loginStyles.authFooter}>
                        <span>Back to <button type="button" className={loginStyles.linkBtn} onClick={() => setAuthView('login')} disabled={resetLoading}>Sign in</button></span>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <div className={styles.heroPanelHeader}>
                  <span className={styles.heroPanelLabel}>RAG pipeline</span>
                  <span className={styles.heroPanelStatus}>Live workspace</span>
                </div>
                <div className={styles.heroPipeline}>
                  {pipelineSteps.map((step, index) => (
                    <React.Fragment key={step.title}>
                      <div className={styles.pipelineStep}>
                        <div className={styles.pipelineStepTop}>
                          <span className={styles.pipelineStepNumber}>{`0${index + 1}`}</span>
                        </div>
                        <div className={styles.pipelineStepMain}>
                          <div className={styles.pipelineIcon}>{step.icon}</div>
                          <p className={styles.pipelineTitle}>{step.title}</p>
                        </div>
                        <p className={styles.pipelineBody}>{step.body}</p>
                      </div>
                      {index < pipelineSteps.length - 1 && <div className={styles.pipelineArrow}>→</div>}
                    </React.Fragment>
                  ))}
                </div>
                <ul className={styles.pipelineSignals}>
                  {pipelineSignals.map((item) => (
                    <li key={item} className={styles.pipelineSignalItem}>
                      {item}
                    </li>
                  ))}
                </ul>
                <div className={styles.heroPanelFooter}>
                  <p>Designed to avoid common RAG failure modes: noisy retrieval, mixed tenants, and unverifiable responses.</p>
                </div>
              </>
            )}
          </div>
        </section>

        <section id="product" className={styles.section}>
          <p className={styles.sectionLabel}>Platform</p>
          <h2 className={styles.sectionTitle}>A premium AI knowledge layer for modern teams</h2>
          <p className={styles.sectionLead}>
            Your answers already exist, but across too many systems. Arihex unifies operational data,
            product documentation, and policy content into one secure layer for retrieval and response.
          </p>
          <div className={styles.featureGrid}>
            <article className={styles.featureCard}>
              <h3>Multi‑source ingestion</h3>
              <p>Connect PostgreSQL, MySQL, spreadsheets, and PDFs without heavy lifting.</p>
              <ul>
                <li>Zero‑copy ingestion for databases</li>
                <li>Automated parsing and chunking for documents</li>
                <li>Incremental syncs to keep answers fresh</li>
              </ul>
            </article>
            <article className={styles.featureCard}>
              <h3>Governed retrieval</h3>
              <p>Every query respects your access model and data boundaries.</p>
              <ul>
                <li>Per‑workspace data isolation</li>
                <li>Role‑based permissions on content</li>
                <li>Audit‑ready logs for compliance</li>
              </ul>
            </article>
            <article className={styles.featureCard}>
              <h3>Agent toolkit</h3>
              <p>Design agents tailored for support, operations, and internal enablement.</p>
              <ul>
                <li>Configurable instructions and personas</li>
                <li>Source‑aware responses with citations</li>
                <li>Analytics on questions, gaps, and usage</li>
              </ul>
            </article>
          </div>
        </section>

        <section id="how-it-works" className={styles.section}>
          <p className={styles.sectionLabel}>How it works</p>
          <h2 className={styles.sectionTitle}>From fragmented knowledge to consistent answers</h2>
          <div className={styles.stepsGrid}>
            <div className={styles.stepCard}>
              <span className={styles.stepBadge}>01</span>
              <h3>Connect your sources</h3>
              <p>Securely link databases and upload files. No code required to get started.</p>
            </div>
            <div className={styles.stepCard}>
              <span className={styles.stepBadge}>02</span>
              <h3>Define access and agents</h3>
              <p>Set permissions by team and configure agents for support, HR, ops, and more.</p>
            </div>
            <div className={styles.stepCard}>
              <span className={styles.stepBadge}>03</span>
              <h3>Ask in natural language</h3>
              <p>Employees and customers get consistent, cited answers powered by your data.</p>
            </div>
          </div>
        </section>

        <section id="hosting" className={styles.section}>
          <p className={styles.sectionLabel}>Hosting & deployment</p>
          <h2 className={styles.sectionTitle}>Deploy where you need it</h2>
          <p className={styles.sectionLead}>
            Choose the deployment model that matches your architecture, governance standards, and compliance obligations.
          </p>
          <div className={styles.hostingGrid}>
            {hostingOptions.map((opt, i) => (
              <article key={i} className={styles.featureCard}>
                <h3>{opt.title}</h3>
                <p>{opt.desc}</p>
              </article>
            ))}
          </div>
        </section>

        <section id="use-cases" className={styles.sectionAlt}>
          <div className={styles.sectionAltInner}>
            <div className={styles.sectionAltCopy}>
              <p className={styles.sectionLabel}>Use cases</p>
              <h2 className={styles.sectionTitle}>Built for real business teams</h2>
              <p className={styles.sectionLead}>
                Not a playground chatbot. Arihex is designed for environments where answers must be
                accurate, explainable, and secure.
              </p>
              <ul className={styles.audienceList}>
                {useCasePoints.map((item) => (
                  <li key={item} className={styles.audiencePill}>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className={styles.sectionAltPanel}>
              <h3 className={styles.panelTitle}>What makes Arihex different</h3>
              <ul className={styles.diffList}>
                {differentiators.map((d) => (
                  <li key={d.title} className={styles.diffItem}>
                    <h4>{d.title}</h4>
                    <p>{d.desc}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section className={styles.section}>
          <p className={styles.sectionLabel}>Outcomes</p>
          <h2 className={styles.sectionTitle}>What teams achieve with Arihex</h2>
          <ul className={styles.outcomeList}>
            {outcomes.map((label) => (
              <li key={label} className={styles.outcomeItem}>
                <span className={styles.outcomeCheck} aria-hidden />
                {label}
              </li>
            ))}
          </ul>
          <p className={styles.sectionLead}>
            Results improve not by adding noise, but by combining high-quality retrieval with enterprise guardrails.
          </p>
        </section>

        <section className={styles.finalCta}>
          <div className={styles.finalCtaInner}>
            <p className={styles.sectionLabel}>Get started</p>
            <h2 className={styles.finalCtaTitle}>Turn your knowledge into an always‑on AI teammate.</h2>
            <p className={styles.finalCtaSubtitle}>
              Create a workspace, connect a data source, and ask your first question in minutes.
            </p>
            <div className={styles.finalCtaActions}>
              <Link to="/?auth=signup" className={styles.heroPrimary}>
                Create workspace
              </Link>
              <Link to="/contact?category=demo" className={`${styles.heroSecondary} ${styles.heroDemoCta}`}>
                Book demo
              </Link>
              <Link to="/about" className={styles.heroSecondary}>
                Learn how Arihex works
              </Link>
            </div>
          </div>
        </section>
      </main>

      <footer className={styles.footer}>
        <div className={styles.footerInner}>
          <p className={styles.footerCopy}>© 2026 arihex.com. All rights reserved.</p>
          <div className={styles.footerLinks}>
            <Link to="/about">About</Link>
            <Link to="/terms">Terms of Service</Link>
            <Link to="/privacy">Privacy Policy</Link>
            <Link to="/contact">Contact Support</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;

